import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Animated, Image, Platform, StatusBar, StyleSheet, Text} from 'react-native';
import LottieView from 'lottie-react-native';
import {LinearGradient} from 'expo-linear-gradient';
import * as ExpoSplashScreen from 'expo-splash-screen';
import i18n from '../../i18n';
import {ThemeContext} from '../helpers/ThemeContext';
import {BRAND} from '../helpers/managementTheme';

const LogoImage = require('../../assets/logo-small.png');
// Hard safety net: if Lottie's onAnimationFinish never fires (a known iOS
// failure mode), force the splash to complete so the app always proceeds.
const SPLASH_TIMEOUT_MS = 3500;
// The navigator only mounts after the splash releases, so this tail is dead
// time on every launch: keep the logo beat short (was 500 ms fade + 1000 ms hold).
const LOGO_FADE_DURATION_MS = 300;
const APP_RELEASE_DELAY_MS = 200;

const SplashScreenComponent = ({ onAnimationFinish }) => {
    // SplashScreen renders OUTSIDE ThemeProvider (see SplashScreenWrapper), so
    // useTheme() would throw here — read the context defensively instead.
    const themeCtx = useContext(ThemeContext);
    const isDark = themeCtx?.isDark;
    const [animationFinished, setAnimationFinished] = useState(false);
    const logoOpacity = useRef(new Animated.Value(0)).current;
    const animationRef = useRef(null);
    const completedRef = useRef(false);
    const releasedRef = useRef(false);
    const timeoutRef = useRef(null);
    const releaseTimeoutRef = useRef(null);
    const hardReleaseTimeoutRef = useRef(null);

    const logSplash = (...args) => {
        console.log('[SplashScreen]', ...args);
    };

    const notifyAppReady = useCallback(() => {
        if (typeof onAnimationFinish === 'function') {
            onAnimationFinish();
        }
    }, [onAnimationFinish]);

    const completeSplash = useCallback(() => {
        if (completedRef.current) {
            logSplash('completeSplash skipped (already completed)');
            return;
        }

        completedRef.current = true;
        logSplash('completeSplash start');

        if (timeoutRef.current) {
            globalThis.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        setAnimationFinished(true);

        // Idempotent release: whichever path fires first (the Animated fade
        // completion callback OR the callback-independent safety timer below)
        // dismisses the splash exactly once.
        const releaseOnce = () => {
            if (releasedRef.current) {
                return;
            }

            releasedRef.current = true;
            notifyAppReady();
        };

        Animated.timing(logoOpacity, {
            toValue: 1,
            duration: LOGO_FADE_DURATION_MS,
            useNativeDriver: true,
        }).start(() => {
            logSplash('Logo fade complete, triggering App callback');
            releaseTimeoutRef.current = globalThis.setTimeout(releaseOnce, APP_RELEASE_DELAY_MS);
        });

        // Safety net: Animated.start()'s completion callback is NOT guaranteed to
        // fire when useNativeDriver is on (a known RN/iOS edge case where the
        // native animation-completion event is dropped). Without this, the orange
        // splash could sit forever. This wall-clock timer releases the app
        // independently of the animation callback; releaseOnce() keeps it from
        // double-firing with the callback path.
        hardReleaseTimeoutRef.current = globalThis.setTimeout(
            releaseOnce,
            LOGO_FADE_DURATION_MS + APP_RELEASE_DELAY_MS + 400,
        );
    }, [logoOpacity, notifyAppReady]);

    const handleAnimationFinish = useCallback(() => {
        logSplash('Lottie onAnimationFinish fired');
        completeSplash();
    }, [completeSplash]);

    useEffect(() => {
        logSplash('Mounted, hiding native splash');
        ExpoSplashScreen.hideAsync().catch(() => undefined);

        timeoutRef.current = globalThis.setTimeout(() => {
            logSplash('Failsafe timeout fired');
            completeSplash();
        }, SPLASH_TIMEOUT_MS);

        return () => {
            if (timeoutRef.current) {
                globalThis.clearTimeout(timeoutRef.current);
            }

            if (releaseTimeoutRef.current) {
                globalThis.clearTimeout(releaseTimeoutRef.current);
            }

            if (hardReleaseTimeoutRef.current) {
                globalThis.clearTimeout(hardReleaseTimeoutRef.current);
            }
        };
    }, [completeSplash]);

    return (
        <LinearGradient colors={BRAND.gradient} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.container}>
            <StatusBar hidden={true} backgroundColor="transparent" barStyle={isDark ? 'light-content' : 'dark-content'} />
            {animationFinished ? (
                <Animated.View style={[styles.logoContainer, { opacity: logoOpacity }]}> 
                    {Platform.OS !== 'web' && (
                        <Image
                            source={LogoImage}
                            style={[styles.logoImage, { width: 150, height: 150 }]}
                        />
                    )}
                    {Platform.OS === 'web' && (
                        <img
                            alt="Happy Solutions Logo"
                            src="https://happy-releases.fra1.cdn.digitaloceanspaces.com/Uploads/logo.png"
                            style={styles.logoImage}
                        />
                    )}
                    <Text style={[styles.logoText, { marginTop: 40 }]}>Happy App</Text>
                    <Text style={styles.subtitle}>{i18n.t('splash_tagline', { defaultValue: 'a Happy Solution software' })}</Text>
                </Animated.View>
            ) : (
                <LottieView
                    ref={animationRef}
                    source={require('../../assets/animations/splashscreen.json')}
                    autoPlay
                    loop={false}
                    speed={0.85}
                    resizeMode="cover"
                    // iOS: HARDWARE render mode regularly renders the splash blank
                    // / frozen AND never fires onAnimationFinish, leaving the app
                    // stuck on the splash. AUTOMATIC lets Lottie pick the safe
                    // path per platform (software on iOS). Android keeps HARDWARE.
                    renderMode={Platform.OS === 'ios' ? 'AUTOMATIC' : 'HARDWARE'}
                    onAnimationFinish={handleAnimationFinish}
                    // If the animation asset itself fails to load/parse, don't sit
                    // on a blank orange screen — complete the splash immediately.
                    onError={handleAnimationFinish}
                    style={styles.lottie}
                />
            )}
        </LinearGradient>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        // Full-bleed at any window size (fold/unfold, split-screen) without re-measuring.
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 0,
        margin: 0,
        // Brand orange (gradient overlay above); also the plain fallback fill.
        backgroundColor: BRAND.base,
    },
    lottie: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: 0,
        margin: 0,
        flex: 1,
    },
    logoContainer: {
        position: 'absolute',
        alignItems: 'center',
        zIndex: 99999999,
    },
    logoImage: {
        width: 150,
        height: 150,
    },
    logoText: {
        fontSize: 40,
        fontWeight: 'bold',
        color: 'white',
    },
    subtitle: {
        fontSize: 15,
        fontWeight: 'bold',
        color: 'white',
    },
});

export default SplashScreenComponent;